import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, RendererFactory2, signal } from '@angular/core';

import { LocalStorageService } from '@shared/services';

export type ThemeMode = 'system' | 'light' | 'dark';

const THEME_COLOR_LS_ITEM = 'theme-color';
const THEME_ATTR = 'data-theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeColorService {
  readonly appTheme = signal<ThemeMode>(this.readSavedTheme(inject(LocalStorageService)));
  readonly colors = computed<CSSStyleDeclaration>(() => this.renderedTheme() && getComputedStyle(this.document.body));

  readonly renderedTheme = computed(() => {
    const appTheme = this.appTheme();
    if (appTheme === 'system') {
      return this.systemTheme();
    }
    return appTheme;
  });

  readonly systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');

  private readonly systemTheme = signal<ThemeMode>(this.systemThemeQuery.matches ? 'dark' : 'light');

  private readonly localStorageService = inject(LocalStorageService);
  private readonly renderer = inject(RendererFactory2).createRenderer(null, null);
  private readonly document = inject(DOCUMENT);

  constructor() {
    this.trackTheme();
    this.trackSystemTheme();
  }

  private setTheme(theme: ThemeMode): void {
    this.localStorageService.setItem(THEME_COLOR_LS_ITEM, theme);
    this.renderer.setAttribute(this.document.body, THEME_ATTR, theme);
  }

  private readSavedTheme(ls: LocalStorageService): ThemeMode {
    const savedTheme = ls.getItem(THEME_COLOR_LS_ITEM);
    if (savedTheme === 'system') {
      return 'system';
    }
    return savedTheme as ThemeMode;
  }

  private trackTheme(): void {
    effect(() => this.setTheme(this.appTheme()));
  }

  private trackSystemTheme(): void {
    this.systemThemeQuery.addEventListener('change', ({ matches }) => {
      if (matches) {
        this.systemTheme.set('dark');
      } else {
        this.systemTheme.set('light');
      }
    });
  }
}
